import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import {
  snoozeAlert,
  snoozeAlertError,
  snoozeAlertSuccess,
  notifyAlert,
  notifyAlertError,
  notifyAlertSuccess,
  notifyAndSnoozeAlert,
  notifyAndSnoozeAlertSuccess,
  notifyAndSnoozeAlertError,
  unsnoozeAlert,
  unsnoozeAlertSuccess,
  unsnoozeAlertError,
  closeAlert,
  closeAlertSuccess,
  closeAlertError,
  closeAlerts,
  closeAlertsSuccess,
  closeAlertsError,
  reopenAlert,
  reopenAlertSuccess,
  reopenAlertError,
} from '../actions/alert.action';
import { AlertService } from 'brain-data/service/alert/alert.service';
import {
  updateFacilityAlertWithSnooze,
  updateFacilityAlertUnsnoozed,
  updateFacilityAlertsClosed,
  updateFacilityAlertReopened,
} from '../actions/facility.actions';

@Injectable()
export class AlertEffects {
  notifyEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notifyAlert),
      switchMap(({ alertId, alertNotification }) =>
        this.alertService.notify(alertId, alertNotification).pipe(
          map(() => notifyAlertSuccess()),
          catchError(error => of(notifyAlertError({ error }))),
        ),
      ),
    );
  });

  snoozeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(snoozeAlert),
      switchMap(({ alertId, days }) =>
        this.alertService.snooze(alertId, days).pipe(
          map(() => snoozeAlertSuccess({ alertId, days })),
          catchError(error => of(snoozeAlertError({ error }))),
        ),
      ),
    );
  });

  notifyAndsnoozeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(notifyAndSnoozeAlert),
      switchMap(({ alertId, days, alertNotification }) =>
        this.alertService.notifyAndSnooze(alertId, days, alertNotification).pipe(
          map(() => notifyAndSnoozeAlertSuccess({ alertId, days, alertNotification })),
          catchError(error => of(notifyAndSnoozeAlertError({ error }))),
        ),
      ),
    );
  });

  unsnoozeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(unsnoozeAlert),
      switchMap(({ alertId }) =>
        this.alertService.unsnooze(alertId).pipe(
          map(() => unsnoozeAlertSuccess({ alertId })),
          catchError(error => of(unsnoozeAlertError({ error }))),
        ),
      ),
    );
  });

  closeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(closeAlert),
      switchMap(({ alertId }) =>
        this.alertService.close(alertId).pipe(
          map(() => closeAlertSuccess({ alertId })),
          catchError(error => of(closeAlertError({ error }))),
        ),
      ),
    );
  });

  closeBatchEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(closeAlerts),
      switchMap(({ alertIds }) =>
        this.alertService.closeBatch(alertIds).pipe(
          map(() => closeAlertsSuccess({ alertIds })),
          catchError(error => of(closeAlertsError({ error }))),
        ),
      ),
    );
  });

  reopenEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reopenAlert),
      switchMap(({ alertId }) =>
        this.alertService.reopen(alertId).pipe(
          map(() => reopenAlertSuccess({ alertId })),
          catchError(error => of(reopenAlertError({ error }))),
        ),
      ),
    );
  });

  updateAlertReopenedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(reopenAlertSuccess),
      map(({ alertId }) => updateFacilityAlertReopened({ alertId })),
    );
  });

  updateAlertUnsnoozedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(unsnoozeAlertSuccess),
      map(({ alertId }) => updateFacilityAlertUnsnoozed({ alertId })),
    );
  });

  updateAlertsClosedEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(closeAlertSuccess),
      map(({ alertId }) => updateFacilityAlertsClosed({ alertIds: [alertId] })),
    );
  });

  updateAlertsClosedBatchEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(closeAlertsSuccess),
      map(({ alertIds }) => updateFacilityAlertsClosed({ alertIds })),
    );
  });

  updateAlertWithSnoozeEffect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(snoozeAlertSuccess, notifyAndSnoozeAlertSuccess),
      map(action => {
        const snoozedUntil = new Date();

        const daysToAdd = Number(action.days);
        if (!isNaN(daysToAdd)) {
          snoozedUntil.setDate(snoozedUntil.getDate() + daysToAdd);
        }

        const hasNotification = 'alertNotification' in action;

        return updateFacilityAlertWithSnooze({
          alertId: action.alertId,
          snoozedUntil: snoozedUntil.toISOString().split('T')[0],
          ...(hasNotification && { alertNotification: action.alertNotification }),
        });
      }),
    );
  });

  constructor(
    private actions$: Actions,
    private alertService: AlertService,
  ) {}
}
